import React, { useContext, useState } from "react";
import { useToast } from "../context/ToastContext";
import flaskApi from "../services/flaskApi";
import "./ToolPage.css";
import { AuthContext } from "../context/AuthContext";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { markRecentGoogleSheet } from "../utils/googleSheetRecents";
import { extractErrorMessage } from "../utils/errorUtils";
import LinesheetSourceSelector from "./shared/LinesheetSourceSelector";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";


// Preset tag options. Selecting any of these marks the product as an
// outlet/clearance item, so the backend omits the default "lookbook" tag.
const PRESET_TAGS = ["outlet", "outlet-sale", "final-sale"];

function SkuBuilderTool() {
  const [linesheetSource, setLinesheetSource] = useState("google");
  const [file, setFile] = useState(null);
  const [selectedGoogleSheet, setSelectedGoogleSheet] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const [rowsToProcess, setRowsToProcess] = useState("");
  const [includeOnMap, setIncludeOnMap] = useState(false);
  const [presetTags, setPresetTags] = useState([]);
  const [customTags, setCustomTags] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { addToast } = useToast();
  const { user } = useContext(AuthContext);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const togglePresetTag = (tag) =>
    setPresetTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );

  const toolInfo = {
    toolName: "SKU Builder",
    description: "Create a product builder sheet from your linesheet, expanding sizes into individual SKUs.",
    steps: [
      "Choose a shared Google Sheet or upload your Linesheet (Excel).",
      "Enter the Sheet Name containing the product data.",
      "Click 'SKU Builder' to download the formatted file.",
    ],
    templates: [
      { name: "Linesheet Template", link: "/templates/download/template_linesheet.xlsx" },
    ],
  };

  const guideTips = [
    "Shared Google Sheets work best when multiple teammates update the same linesheet during the week.",
    "Enter the exact tab name from the linesheet so the builder pulls the correct vendor data.",
    "After a successful Google Sheets run, that sheet will move toward the top of the list for your account.",
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();
    const hasLinesheet =
      linesheetSource === "google"
        ? Boolean(selectedGoogleSheet?.id)
        : Boolean(file);

    if (!hasLinesheet || !sheetName) {
      addToast("Please provide a linesheet source and sheet name.", "error");
      return;
    }

    const maxSize = 100 * 1024 * 1024;
    if (linesheetSource === "upload" && file.size > maxSize) {
      addToast("File is too large. Maximum size is 100MB.", "error");
      return;
    }

    const formData = new FormData();
    if (linesheetSource === "google") {
      formData.append("google_linesheet_file_id", selectedGoogleSheet.id);
      formData.append("google_linesheet_file_name", selectedGoogleSheet.name);
    } else {
      formData.append("file", file);
    }
    formData.append("sheet_name", sheetName);
    if (rowsToProcess) {
      formData.append("rows_to_process", rowsToProcess);
    }
    if (includeOnMap) {
      formData.append("include_on_map", "true");
    }
    // Merge preset tag checkboxes with any free-text tags, de-duplicated.
    const typedTags = customTags
      .split(/[\n,]+/)
      .map((tag) => tag.trim())
      .filter(Boolean);
    const mergedTags = [...new Set([...presetTags, ...typedTags])];
    if (mergedTags.length) {
      formData.append("custom_tags", mergedTags.join(", "));
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      const response = await flaskApi.post("/product/generate-sku-builder-ui", formData, {
        responseType: "blob",
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      if (
        response.data.type === "application/json" ||
        (response.headers["content-type"] &&
          response.headers["content-type"].includes("application/json"))
      ) {
        const text = await response.data.text();
        const json = JSON.parse(text);
        addToast(`Error: ${json.error}`, "error");
        return;
      }

      if (
        response.data.type === "text/html" ||
        (response.headers["content-type"] &&
          response.headers["content-type"].includes("text/html"))
      ) {
        const text = await response.data.text();
        addToast("Server Error: Received HTML instead of file.", "error");
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      const filename = extractedFilename || "SKU_Builder.xlsx";

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      if (linesheetSource === "google" && selectedGoogleSheet?.id) {
        markRecentGoogleSheet(user?.employeeId, selectedGoogleSheet);
      }

      addToast("File downloaded successfully.", "success");
    } catch (error) {
      const errorMessage = await extractErrorMessage(error, "Error generating SKU Builder.");
      addToast(errorMessage, "error");
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <>
      <ToolPageLayout
        title="SKU Builder"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form tool-form-split">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Builder Settings</h3>
              </div>

              <div className="tool-field-stack">
                <label className="tool-label">Linesheet Tab Name</label>
                <input
                  type="text"
                  className="tool-input"
                  value={sheetName}
                  onChange={(event) => setSheetName(event.target.value)}
                  placeholder="e.g. Birkenstock"
                  required
                />
              </div>

              <div className="tool-field-stack">
                <label className="tool-label">Rows to Process (Optional)</label>
                <input
                  type="text"
                  className="tool-input"
                  value={rowsToProcess}
                  onChange={(event) => setRowsToProcess(event.target.value)}
                  placeholder="e.g. 6-10, 12, 14 (Leave empty for all)"
                />
              </div>

              <div className="tool-field-stack">
                <label className="tool-label">Tag Options</label>
                <div className="shopify-checkbox-row">
                  <label className="shopify-checkbox-label">
                    <input
                      type="checkbox"
                      checked={includeOnMap}
                      onChange={(event) => setIncludeOnMap(event.target.checked)}
                    />
                    Add on-map tag
                  </label>
                  {PRESET_TAGS.map((tag) => (
                    <label className="shopify-checkbox-label" key={tag}>
                      <input
                        type="checkbox"
                        checked={presetTags.includes(tag)}
                        onChange={() => togglePresetTag(tag)}
                      />
                      {tag}
                    </label>
                  ))}
                </div>
                {presetTags.length > 0 && (
                  <p className="tool-helper-text">
                    Outlet/clearance tags selected — the default "lookbook" tag will not be applied.
                  </p>
                )}
              </div>

              <div className="tool-field-stack">
                <label className="tool-label">Custom Tags (Optional)</label>
                <textarea
                  className="tool-input"
                  value={customTags}
                  onChange={(event) => setCustomTags(event.target.value)}
                  placeholder="e.g. preorder, staff-pick"
                />
                <p className="tool-helper-text">Comma- or line-separated tags added to every generated row.</p>
              </div>
            </section>

            <div className="tool-action-stack">
              <button type="submit" className="tool-btn-primary" disabled={loading}>
                {loading ? "Processing..." : "SKU Builder"}
              </button>

              <ToolProgressPanel
                loading={loading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading linesheet source..."
                processingMessage="Generating SKU Builder..."
              />
            </div>
          </div>

          <div className="tool-form-side">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Linesheet Source</h3>
              </div>

              <LinesheetSourceSelector
                source={linesheetSource}
                onSourceChange={setLinesheetSource}
                uploadFile={file}
                onUploadFileChange={setFile}
                selectedGoogleSheet={selectedGoogleSheet}
                onSelectedGoogleSheetChange={setSelectedGoogleSheet}
                uploadLabel="Upload Linesheet (Max 100MB)"
              />
            </section>
          </div>
        </form>
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default SkuBuilderTool;

